import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { X, FileSpreadsheet, FileCode, FileText, File } from 'lucide-react'
import { Button } from '@/components/ui/Button'

export type ExportFormat = 'xls' | 'xml' | 'pdf' | 'txt'

interface ExportModalProps {
  isOpen: boolean
  period: string
  onClose: () => void
  onExport: (format: ExportFormat) => void
  isExporting?: boolean
}

export function ExportModal({
  isOpen,
  period,
  onClose,
  onExport,
  isExporting = false
}: ExportModalProps) {
  const { t } = useTranslation()

  const formats: { id: ExportFormat; label: string; icon: typeof FileSpreadsheet; description: string }[] = [
    {
      id: 'xls',
      label: 'Excel (XLS)',
      icon: FileSpreadsheet,
      description: t('reports.formatXLS', 'Formato Excel para análise de dados')
    },
    {
      id: 'xml',
      label: 'XML',
      icon: FileCode,
      description: t('reports.formatXML', 'Formato XML para integração')
    },
    {
      id: 'pdf',
      label: 'PDF',
      icon: FileText,
      description: t('reports.formatPDF', 'Formato PDF para impressão')
    },
    {
      id: 'txt',
      label: 'TXT',
      icon: File,
      description: t('reports.formatTXT', 'Formato texto simples (simulação)')
    }
  ]

  const handleFormatSelect = (format: ExportFormat) => {
    onExport(format)
  }

  const getPeriodLabel = () => {
    switch (period) {
      case 'week':
        return t('reports.week')
      case 'month':
        return t('reports.month')
      case 'year':
        return t('reports.year')
      default:
        return period
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 pb-20 md:pb-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl w-full max-w-md max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-bold text-text-primary">
                  {t('reports.exportReport', 'Exportar Relatório')}
                </h2>
                <p className="text-sm text-text-secondary mt-1">
                  {t('reports.period', 'Período')}: {getPeriodLabel()}
                </p>
              </div>
              <button
                onClick={onClose}
                disabled={isExporting}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5 text-text-secondary" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-text-secondary mb-4">
                {t('reports.selectFormat', 'Selecione o formato de exportação')}
              </p>

              <div className="space-y-2">
                {formats.map((format) => {
                  const IconComponent = format.icon
                  return (
                    <button
                      key={format.id}
                      onClick={() => handleFormatSelect(format.id)}
                      disabled={isExporting}
                      className="w-full flex items-center space-x-4 p-4 rounded-xl border border-gray-200 hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-text-primary">{format.label}</h3>
                        <p className="text-xs text-text-secondary">{format.description}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-6 border-t border-gray-100">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isExporting}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

